/**
  * @class mmodOfficeWfDelicts
  * @description
  *
  * # Purpose
  * Module displays delict records imported from vehicles.
  * Also allows export and import of delict records.
  */

import mmodWorkflow from 'mmodWorkflow'

export default class mmodOfficeWfDelicts extends mmodWorkflow {
  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates an instance of the class.
  * @param {string} [id] - ID under which the object will listen for messages
  * @param {string} [cfg] - object initial configuration
  * @param {string} [cfg_schema] - additional schema properties from derived classes
  * @param {string} [dependencies] - additional dependencies from derived classes
  */
  constructor(id, cfg, cfg_schema, dependencies) {
    dependencies = _.union([], dependencies)
    cfg_schema = _.extend({
      init: {
        type: 'object',
        default: { type: 'full' },
        description: 'Initial window setup; same structure as required by `mmWindowSet`',
      },
      workflow_server: {
        type: 'string',
        default: ':msvrDelicts',
        description: 'Listener acting as the server-side for the workflow',
      },
      tabular: {
        type: 'boolean',
        default: true,
      },
      delay_initial_search: {
        type: 'integer',
        description: 'delay of initial search',
        default: 20,
      },
      export_mime_types: {
        description: 'mapping of export actions to MIME types',
        type: 'object',
        default: {
          EXPORT_MP4: 'video/mp4',
          EXPORT_MM: 'application/octet-stream',
          EXPORT_TRM: 'application/json',
        }
      },
      export_extensions: {
        description: 'mapping of export actions to file extensions',
        type: 'object',
        default: {
          EXPORT_MP4: 'mp4',
          EXPORT_MM: 'mm',
          EXPORT_TRM: 'json',
        }
      },
    }, cfg_schema)
    super(id, cfg, cfg_schema, dependencies)
  } // constructor

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sent by &lt;mele-container> or derived class to parent module when form is submitted
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} params.holder - key attribute of the form
  * @param {string} [params.button] - key attribute of the button
  * @param {string} [params.datakey] - key attribute of the content
  * @param {object} params.values - hash of key:value of contained editable inputs
  * @param {boolean} [params.invalid] - true if any of form's inputs is invalid
  * @param {boolean} [params.conflict] - true if any of form's inputs has conflict
  */
  async mmFormSubmit(sender, params) {
    const { button } = params
    if(button === 'import_file') {
      this.publish('mmodOfficeImport', 'mmImportPrompt', {
        folders: false,
        selected: ['mmDelict'],
      })
    }else if(button === 'import_folder') {
      this.publish('mmodOfficeImport', 'mmImportPrompt', {
        folders: true,
        selected: ['mmDelict'],
      })
    }else if(/^ACTION:EXPORT_/.test(button)) {
      const action = button.replace('ACTION:', '')
      this.exportLocal(action, params.datakey)
    }else if(button === 'window_print') {
      window.print()
    }else{
      super.mmFormSubmit(sender, params)
    }
  } // mmFormSubmit

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Export delict record as .mm binary file
  * @param {string} id - delict record ID
  */
  async exportLocal(action, id) {
    this.modalWait('{{Please wait}}...', `$wf.action.${action}`)
    const mime_type = this.config.export_mime_types?.[action] || 'application/octet-stream'
    const ext = this.config.export_extensions?.[action] || 'bin'
    try{
      const reply = await this.request(this.config.workflow_server, 'mmAction', {
        action,
        id,
      })
      const binary = reply?.params?.list?.[0]?.export
      if(binary && binary.length > 0 && typeof binary !== 'string') {
        const blob = new Blob([binary], { type: mime_type })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = id + '.' + ext
        a.click()
        URL.revokeObjectURL(url)
        this.modalCloseDefault()
      }else {
        this.modalError(binary || 'wf.delicts.export.NoData', 'wf.delicts.export.Error')
      }
    }catch(e) {
      this.modalError(String(e), 'wf.delicts.export.Error')
    }
  } // exportLocal

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * called on form_ready hook of detail form
  */
  async formready(form_name, record, form) {
    if(form_name === 'read') {
      form.record_data = record
      this.refreshMainImage(form)
    }else if(form_name === 'print') {
      const printed_by = form.find('mele-input[key=printed_by]')
      if(printed_by) printed_by.value = this.user_id
      const printed_at = form.find('mele-input[key=printed_at]')
      if(printed_at) printed_at.value = Date.now()
      form.record_data = record
    }
  } // formready

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Refreshes main image display on read form - shows enlarged image on click
  */
  refreshMainImage(form) {
    const data = form?.record_data
    if(!data) return
    const img_el = form?.find('mele-input[key=jpeg]')
    if(!img_el) return
    const img = img_el?.find('img')
    if(img) {
      img.onclick = this.showEnlargedImage.bind(this, img)
    }
  } // refreshMainImage

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Show enlarged image in modal
  */
  showEnlargedImage(source_img) {
    this.modalOpen({
      key: 'mmodOfficeWfDelictsPreview',
      position: 'center',
      outclick: ':modal.cancel',
      inclick: ':modal.cancel',
      class: 'modal-preview',
      items: [
        { tag: 'img', src: source_img.src },
      ],
    })
  } // showEnlargedImage

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sends by &lt;mele-button> to parent module when a button is clicked
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.holder] - key attribute of button's parent container
  * @param {string} params.button - key attribute of the button
  */
  mmButtonClicked(sender, params) {
    const { button } = params
    if(button === 'snapshot_download') {
      const form = this.el.find('mele-view[key=read][role*=bottom]')
      if(form && form.record_data) {
        const data = form.record_data
        const jpeg = data?.jpeg
        if(jpeg) {
          const el = document.createElement('a')
          el.style.display = 'none'
          el.setAttribute('href', jpeg)
          el.setAttribute('download', this.exportFileName(data) + '.jpg')
          document.body.appendChild(el)
          el.click()
          document.body.removeChild(el)
        }
      }
    }else{
      super.mmButtonClicked(sender, params)
    }
  } // mmButtonClicked

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Generate export filename for delict record
  * @param {object} data - delict record data
  * @returns {string} filename without extension
  */
  exportFileName(data) {
    const app_id = data?.app_id || 'unknown'
    const channel = data?.channel || 0

    let seq = parseInt(data?.seq)
    if(seq > 0) {
      seq = String(seq).padStart(6, '0')
    }else {
      seq = 'unknownseq'
    }

    const event_dt = parseInt(data?.event_dt)
    if(!(event_dt > 0)) return 'delict'

    const d = new Date(event_dt / 1000)
    const dt_str =
      String(d.getFullYear()).padStart(4, 0) + '-' +
      String(d.getMonth() + 1).padStart(2, 0) + '-' +
      String(d.getDate()).padStart(2, 0) + '_' +
      String(d.getHours()).padStart(2, 0) + '-' +
      String(d.getMinutes()).padStart(2, 0) + '-' +
      String(d.getSeconds()).padStart(2, 0)
    return `mosy_delict_${app_id}_${channel}_${event_dt}_${seq}_${dt_str}`
  } // exportFileName
} // mmodOfficeWfDelicts
